import React, { useEffect, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Col, Row, Table, Dropdown, ProgressBar } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getToken, getUserId } from '../services/authorize';
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from '../contexts/LoadingContext';

export default function AdvisorGrade() {
  const { setLoading } = useLoading();
  const currentSkin = (localStorage.getItem('skin-mode')) ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);

  const switchSkin = (skin) => {
    if (skin === 'dark') {
      const btnWhite = document.getElementsByClassName('btn-white');
      for (const btn of btnWhite) {
        btn.classList.add('btn-outline-primary');
        btn.classList.remove('btn-white');
      }
    } else {
      const btnOutlinePrimary = document.getElementsByClassName('btn-outline-primary');
      for (const btn of btnOutlinePrimary) {
        btn.classList.remove('btn-outline-primary');
        btn.classList.add('btn-white');
      }
    }
    const tables = document.getElementsByTagName('table');
    for (const table of tables) {
      const theads = table.querySelectorAll('thead');
      if (skin === 'dark') {
        for (const thead of theads) {
          thead.classList.remove('table-secondary');
          thead.classList.add('table-primary');
        }
        table.classList.add('table-dark');
      } else {
        table.classList.remove('table-dark');
        for (const thead of theads) {
          thead.classList.remove('table-primary');
          thead.classList.add('table-secondary');
        }
      }
    }
  };

  switchSkin(skin);
  useEffect(() => { switchSkin(skin); }, [skin]);

  const [data, setData] = useState([]);
  const [yearId, setYearId] = useState(() => localStorage.getItem('yearId') || null);
  const [selectedYear, setSelectedYear] = useState("");
  const [yearList, setYearList] = useState([]);

  useEffect(() => {
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/getStudyYear`, {
      headers: { authorization: `Bearer ${getToken()}` }
    })
    .then(res => {
      const list = res.data;
      setYearList(list);
      let initialYearId = localStorage.getItem('yearId');
      if (!initialYearId && list.length > 0) {
        initialYearId = list[0].year_id;
        localStorage.setItem('yearId', initialYearId);
      }
      setYearId(initialYearId);
      const selected = list.find(y => String(y.year_id) === String(initialYearId));
      setSelectedYear(selected ? `ปีการศึกษา ${selected.year_year}` : "");
    })
    .catch(() => {})
    .finally(() => setLoading(false));
  }, [setLoading]);

  useEffect(() => {
    if (yearId === null) return;
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/getSubjectsInAdvisorRoom/${getUserId()}/${yearId}`, {
      headers: { authorization: `Bearer ${getToken()}` }
    })
    .then(res => {
      setData(res.data);
    })
    .catch(err => {
      if (err.response?.status === 404) {
        setData([]);
      } else {
        Swal.fire({
          title: err.response?.data?.title || 'เกิดข้อผิดพลาด',
          text: err.response?.data?.message || err.message,
          icon: 'error'
        });
        setData([]);
      }
    })
    .finally(() => setLoading(false));
  }, [yearId, setLoading]);

  const handleSelectYear = (id) => {
    const selected = yearList.find(y => y.year_id === id);
    if (selected) {
      setYearId(id);
      setSelectedYear(`ปีการศึกษา ${selected.year_year}`);
      localStorage.setItem('yearId', id);
    }
  };

  const subjectTableSection = (semester) => {
    const rows = data.filter(d => String(d.sub_semester) === String(semester));
    return (
      <>
        <thead>
          <tr className="text-center">
            <th colSpan={5}>ภาคเรียนที่ {semester}</th>
          </tr>
        </thead>
        <thead>
          <tr>
            <th className="text-center" style={{ width: "90px" }}>รหัสวิชา</th>
            <th>ชื่อวิชา</th>
            <th className="text-center">อาจารย์ผู้สอน</th>
            <th className="text-center" style={{ width: "160px" }}>ส่งเกรดแล้ว</th>
            <th className="text-center" style={{ width: "160px" }}></th>
          </tr>
        </thead>
        <tbody>
          {rows.length > 0 ? rows.map(item => {
            const pct = item.total_students > 0
              ? Math.round((item.submitted_count / item.total_students) * 100)
              : 0;
            return (
              <tr key={item.teach_id}>
                <td className="text-center">{item.sub_subid}</td>
                <td>{item.sub_subname}{item.sub_type === 'extra' ? ' (เพิ่มเติม)' : ''}</td>
                <td className="text-center">{item.teaching_teacher_name}</td>
                <td className="text-center">
                  <div className="d-flex align-items-center gap-2">
                    <ProgressBar
                      now={pct}
                      variant={pct === 100 ? 'success' : pct >= 50 ? 'warning' : 'danger'}
                      style={{ flex: 1, height: '8px' }}
                    />
                    <small>{item.submitted_count}/{item.total_students}</small>
                  </div>
                </td>
                <td className="text-center">
                  <Link to={`/advisor/view-grade/${item.teach_id}`}>
                    <i className="ri-eye-line"></i> แสดงผลการเรียน
                  </Link>
                </td>
              </tr>
            );
          }) : (
            <tr><td colSpan={5} className="text-center">ไม่พบข้อมูล</td></tr>
          )}
        </tbody>
      </>
    );
  };

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">ที่ปรึกษาห้องเรียน</Link></li>
              <li className="breadcrumb-item active" aria-current="page">ผลการเรียนรายวิชา</li>
            </ol>
            <h4 className="main-title mb-0">ผลการเรียนรายวิชา</h4>
          </div>
          <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
            <Dropdown>
              <Dropdown.Toggle variant="primary" id="dropdown-year">
                {selectedYear || "เลือกปีการศึกษา"}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {yearList.map(item => (
                  <Dropdown.Item key={item.year_id} onClick={() => handleSelectYear(item.year_id)}>
                    ปีการศึกษา {item.year_year}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        <Row className="g-12 mb-4">
          <Col xl="12">
            <Card className="card-one">
              <Card.Header>
                <Card.Title as="h6">{selectedYear}</Card.Title>
              </Card.Header>
              <Card.Body className="p-3">
                <Table responsive hover className="mb-0">
                  {subjectTableSection('1')}
                  {subjectTableSection('2')}
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Footer />
      </div>
    </React.Fragment>
  );
}
